// src/pages/rh/Contrats.jsx
import React, { useState, useEffect, useMemo } from "react";
import { 
    Card, Table, Row, Col, Form, Button, Pagination, 
    Modal, Alert, Spinner, Badge 
} from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import Footer from "../../layouts/Footer";
import Header from "../../layouts/Header";
import { formatDate } from "../../utils/formatters";
import { API_BASE_URL } from "../../constants/api";

export default function Contrats() {
    const navigate = useNavigate();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filterText, setFilterText] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [contratToDelete, setContratToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [showSuccessAlert, setShowSuccessAlert] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');

    useEffect(() => {
        fetchContrats();
    }, []);

    const showSuccess = (message) => {
        setSuccessMessage(message);
        setShowSuccessAlert(true);
        setTimeout(() => setShowSuccessAlert(false), 3000);
    };

    const fetchContrats = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_BASE_URL}/contrats.php`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (!response.ok) {
                if (response.status === 401) {
                    localStorage.removeItem("token");
                    localStorage.removeItem("user");
                    navigate("/pages/signin2");
                    throw new Error("Session expirée");
                }
                throw new Error(`Erreur HTTP: ${response.status}`);
            }
            
            const result = await response.json();
            setData(result.data || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteClick = (contrat) => {
        setContratToDelete(contrat);
        setShowDeleteModal(true);
    };

    const handleConfirmDelete = async () => {
        if (!contratToDelete) return;
        
        setDeleting(true);
        
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_BASE_URL}/contrats.php?id=${contratToDelete.id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            const result = await response.json();
            
            if (result.success) {
                setShowDeleteModal(false);
                setContratToDelete(null);
                fetchContrats();
                showSuccess("Contrat supprimé avec succès");
            } else {
                alert(result.message || "Erreur lors de la suppression");
                setShowDeleteModal(false);
            }
        } catch (err) {
            alert("Erreur de connexion au serveur");
            setShowDeleteModal(false);
        } finally {
            setDeleting(false);
        }
    };

    const getStatutBadge = (contrat) => {
        const today = new Date();
        const dateDebut = new Date(contrat.date_debut);
        const dateFin = contrat.date_fin ? new Date(contrat.date_fin) : null;
        
        if (contrat.statut === 'termine') {
            return { variant: 'secondary', text: 'Terminé', icon: 'ri-checkbox-circle-line' };
        }
        if (dateFin && dateFin < today) {
            return { variant: 'danger', text: 'Expiré', icon: 'ri-error-warning-line' };
        }
        if (dateDebut > today) {
            return { variant: 'warning', text: 'À venir', icon: 'ri-time-line' };
        }
        return { variant: 'success', text: 'Actif', icon: 'ri-checkbox-circle-line' };
    };

    const filteredItems = useMemo(() => {
        let filtered = data.filter(item => {
            const textMatch = 
                (item.employe_nom || '').toLowerCase().includes(filterText.toLowerCase()) ||
                (item.employe_prenom || '').toLowerCase().includes(filterText.toLowerCase()) ||
                (item.employeur_nom || '').toLowerCase().includes(filterText.toLowerCase());
            return textMatch;
        });
        
        filtered.sort((a, b) => new Date(b.date_debut) - new Date(a.date_debut));
        return filtered;
    }, [filterText, data]);

    const totalPages = Math.ceil(filteredItems.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const currentItems = filteredItems.slice(startIndex, startIndex + itemsPerPage);

    if (loading && !data.length) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 100px)' }}>
                        <div className="text-center">
                            <div className="spinner-border text-primary mb-3" style={{ width: '3rem', height: '3rem', color: '#21ACCD' }} role="status">
                                <span className="visually-hidden">Chargement...</span>
                            </div>
                            <h5 className="text-muted">Chargement des contrats...</h5>
                        </div>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                <div className="container-fluid">
                    {showSuccessAlert && (
                        <Alert variant="success" onClose={() => setShowSuccessAlert(false)} dismissible className="mb-4 alert-modern">
                            <i className="ri-checkbox-circle-line me-2 fs-5"></i>
                            {successMessage}
                        </Alert>
                    )}

                    {/* En-tête */}
                    <div className="d-md-flex align-items-center justify-content-between mb-4">
                        <div>
                            <div className="d-flex align-items-center gap-2 mb-2">
                                <div className="p-2 bg-primary bg-opacity-10 rounded-3">
                                    <i className="ri-file-copy-line fs-20 text-primary" style={{ color: '#21ACCD' }}></i>
                                </div>
                                <div>
                                    <h4 className="main-title mb-0 fw-bold">Gestion des contrats</h4>
                                    <p className="text-muted small mb-0">Suivi des contrats de travail</p>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 mt-3 mt-md-0">
                            <Button 
                                variant="outline-primary" 
                                size="sm"
                                onClick={fetchContrats}
                                disabled={loading}
                                className="rounded-pill px-3 d-flex align-items-center gap-1"
                                style={{ color: '#21ACCD', borderColor: '#21ACCD' }}
                            >
                                <i className={`ri-refresh-line ${loading ? 'fa-spin' : ''}`}></i>
                                Actualiser
                            </Button>
                            <Button 
                                style={{ background: '#21ACCD', border: '0', color: 'white' }}
                                as={Link} 
                                to="/rh/contratsNouveau" 
                                size="sm"
                                className="rounded-pill px-3 d-flex align-items-center gap-1"
                            >
                                <i className="ri-add-line"></i>
                                Nouveau contrat
                            </Button>
                            <Button 
                                variant="outline-warning" 
                                size="sm"
                                as={Link}
                                to="/rh/contratsAlertes"
                                className="rounded-pill px-3 d-flex align-items-center gap-1"
                            >
                                <i className="ri-alert-line"></i>
                                Alertes
                                {filteredItems.filter(c => {
                                    const dateFin = c.date_fin ? new Date(c.date_fin) : null;
                                    const today = new Date();
                                    const weekLater = new Date();
                                    weekLater.setDate(today.getDate() + 7);
                                    return dateFin && dateFin >= today && dateFin <= weekLater && c.statut === 'actif';
                                }).length > 0 && (
                                    <Badge bg="danger" className="ms-1 rounded-pill">
                                        {filteredItems.filter(c => {
                                            const dateFin = c.date_fin ? new Date(c.date_fin) : null;
                                            const today = new Date();
                                            const weekLater = new Date();
                                            weekLater.setDate(today.getDate() + 7);
                                            return dateFin && dateFin >= today && dateFin <= weekLater && c.statut === 'actif';
                                        }).length}
                                    </Badge>
                                )}
                            </Button>
                        </div>
                    </div>

                    {/* Filtres */}
                    <Card className="border-0 shadow-sm rounded-3 mb-4">
                        <Card.Body className="py-3 px-4">
                            <Row className="align-items-center g-3">
                                <Col md="6">
                                    <div className="position-relative">
                                        <i className="ri-search-line position-absolute top-50 start-0 translate-middle-y ms-3 text-muted fs-14"></i>
                                        <Form.Control
                                            type="text"
                                            placeholder="Rechercher par employé ou employeur..."
                                            value={filterText}
                                            onChange={e => setFilterText(e.target.value)}
                                            className="ps-5 rounded-pill"
                                        />
                                    </div>
                                </Col>
                                <Col md="4">
                                    <Form.Select 
                                        value={itemsPerPage} 
                                        onChange={(e) => setItemsPerPage(Number(e.target.value))}
                                        className="rounded-pill px-3"
                                        style={{ width: '120px' }}
                                    >
                                        <option value={5}>5</option>
                                        <option value={10}>10</option>
                                        <option value={25}>25</option>
                                        <option value={50}>50</option>
                                    </Form.Select>
                                </Col>
                                <Col md="2">
                                    <span className="badge bg-light text-dark rounded-pill px-3 py-2">
                                        <i className="ri-stack-line me-1"></i>
                                        {filteredItems.length} contrat(s)
                                    </span>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* Tableau des contrats */}
                    <Card className="border-0 shadow-sm rounded-3 overflow-hidden">
                        <Card.Header className="border-0 py-3" style={{ background: '#21ACCD' }}>
                            <div className="d-flex justify-content-between align-items-center">
                                <div className="d-flex align-items-center">
                                    <i className="ri-file-copy-line text-white me-2 fs-5"></i>
                                    <span className="fw-semibold text-white">Liste des contrats</span>
                                    <Badge bg="light" text="dark" className="ms-2 rounded-pill">
                                        {filteredItems.length} au total
                                    </Badge>
                                </div>
                            </div>
                        </Card.Header>
                        <Card.Body className="p-0">
                            <div className="table-responsive">
                                <Table hover className="align-middle mb-0">
                                    <thead>
                                        <tr>
                                            <th className="ps-4">Employé</th>
                                            <th>Employeur</th>
                                            <th>Date début</th>
                                            <th>Date fin</th>
                                            <th>Type</th>
                                            <th>Statut</th>
                                            <th className="text-center pe-4">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentItems.length > 0 ? currentItems.map((contrat) => {
                                            const statut = getStatutBadge(contrat);
                                            const today = new Date();
                                            const dateFin = contrat.date_fin ? new Date(contrat.date_fin) : null;
                                            const isExpiringSoon = dateFin && dateFin >= today && dateFin <= new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);
                                            
                                            return (
                                                <tr key={contrat.id} className={isExpiringSoon ? 'table-warning' : ''}>
                                                    <td className="ps-4">
                                                        <div className="fw-semibold">{contrat.employe_prenom} {contrat.employe_nom}</div>
                                                        <small className="text-muted">{contrat.employe_email}</small>
                                                    </td>
                                                    <td>
                                                        <div>{contrat.employeur_nom}</div>
                                                        <small className="text-muted">{contrat.employeur_email}</small>
                                                    </td>
                                                    <td>{formatDate(contrat.date_debut)}</td>
                                                    <td>
                                                        {contrat.date_fin ? formatDate(contrat.date_fin) : 'CDI'}
                                                        {isExpiringSoon && (
                                                            <Badge bg="warning" className="ms-2 rounded-pill text-white">
                                                                <i className="ri-alert-line me-1"></i>Expire bientôt
                                                            </Badge>
                                                        )}
                                                    </td>
                                                    <td>
                                                        <Badge bg={contrat.date_fin ? 'info' : 'primary'} className="rounded-pill">
                                                            {contrat.date_fin ? 'CDD' : 'CDI'}
                                                        </Badge>
                                                    </td>
                                                    <td>
                                                        <Badge bg={statut.variant} className="rounded-pill text-white" style={{color:'white !important'}}>
                                                            <i className={`${statut.icon} me-1`}></i>
                                                            {statut.text}
                                                        </Badge>
                                                    </td>
                                                    <td className="text-center pe-4">
                                                        <div className="d-flex gap-2 justify-content-center">
                                                            <button 
                                                                className="action-icon action-icon-primary" 
                                                                onClick={() => navigate(`/rh/contrats/${contrat.id}`)} 
                                                                title="Voir détails"
                                                            >
                                                                <i className="ri-eye-line"></i>
                                                            </button>
                                                            <button 
                                                                className="action-icon action-icon-danger" 
                                                                onClick={() => handleDeleteClick(contrat)} 
                                                                title="Supprimer"
                                                            >
                                                                <i className="ri-delete-bin-line"></i>
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        }) : (
                                            <tr>
                                                <td colSpan="7" className="text-center py-5">
                                                    <i className="ri-inbox-line fs-48 text-muted mb-3 d-block"></i>
                                                    <h6 className="mb-1">Aucun contrat trouvé</h6>
                                                    <p className="text-muted small mb-0">Cliquez sur "Nouveau contrat" pour en créer un</p>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        </Card.Body>
                    </Card>

                    {/* Pagination */}
                    {totalPages > 1 && !loading && (
                        <div className="d-flex justify-content-between align-items-center mt-4 px-3">
                            <div className="text-muted small">
                                Affichage de {startIndex + 1} à {Math.min(startIndex + itemsPerPage, filteredItems.length)} sur {filteredItems.length} contrats
                            </div>
                            <Pagination className="mb-0">
                                <Pagination.Prev onClick={() => setCurrentPage(p => Math.max(1, p-1))} disabled={currentPage === 1} />
                                {[...Array(Math.min(5, totalPages))].map((_, i) => {
                                    let pageNum;
                                    if (totalPages <= 5) pageNum = i + 1;
                                    else if (currentPage <= 3) pageNum = i + 1;
                                    else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                                    else pageNum = currentPage - 2 + i;
                                    return (
                                        <Pagination.Item key={i} active={pageNum === currentPage} onClick={() => setCurrentPage(pageNum)}>
                                            {pageNum}
                                        </Pagination.Item>
                                    );
                                })}
                                <Pagination.Next onClick={() => setCurrentPage(p => Math.min(totalPages, p+1))} disabled={currentPage === totalPages} />
                            </Pagination>
                        </div>
                    )}
                </div>
                <Footer />
            </div>

            {/* Modal de suppression */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered className="modern-modal">
                <Modal.Header closeButton className="border-0 pb-0" style={{ background: '#dc2626' }}>
                    <Modal.Title className="w-100">
                        <div className="text-center">
                            <div className="confirm-icon mb-3">
                                <i className="ri-delete-bin-line text-white" style={{ fontSize: '4rem' }}></i>
                            </div>
                            <h3 className="fw-bold text-white">Confirmer la suppression</h3>
                        </div>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="pt-4 text-center">
                    <p className="text-muted mb-3">Êtes-vous sûr de vouloir supprimer ce contrat ?</p>
                    <div className="bg-light p-3 rounded-3 mb-3">
                        <strong className="text-primary d-block">{contratToDelete?.employe_prenom} {contratToDelete?.employe_nom}</strong>
                        <small className="text-muted">{contratToDelete?.employeur_nom}</small>
                        <div className="mt-1">Du {formatDate(contratToDelete?.date_debut)} au {contratToDelete?.date_fin ? formatDate(contratToDelete?.date_fin) : 'CDI'}</div>
                    </div>
                </Modal.Body>
                <Modal.Footer className="border-0 pt-0 pb-4 justify-content-center">
                    <Button variant="outline-secondary" onClick={() => setShowDeleteModal(false)} className="rounded-pill px-4">
                        Annuler
                    </Button>
                    <Button variant="danger" onClick={handleConfirmDelete} disabled={deleting} className="rounded-pill px-5">
                        {deleting ? (
                            <>
                                <Spinner size="sm" className="me-2" />
                                Suppression...
                            </>
                        ) : (
                            'Confirmer'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            <style>{`
                .action-icon { width: 32px; height: 32px; border-radius: 8px; display: inline-flex; align-items: center; justify-content: center; background: #f8f9fa; border: 1px solid #e9ecef; color: #6c757d; transition: all 0.2s; cursor: pointer; }
                .action-icon:hover { background: #21ACCD; border-color: #21ACCD; color: white; }
                .action-icon-primary:hover { background: #21ACCD; border-color: #21ACCD; color: white; }
                .action-icon-danger:hover { background: #dc3545; border-color: #dc3545; color: white; }
                .rounded-3 { border-radius: 12px !important; }
                .rounded-pill { border-radius: 50px !important; }
                .fs-20 { font-size: 1.25rem; }
                .fs-48 { font-size: 3rem; }
                .alert-modern { border: none; border-radius: 12px; padding: 1rem 1.5rem; box-shadow: 0 4px 8px rgba(0,0,0,0.05); }
                .modern-modal .modal-content { border-radius: 20px; border: none; box-shadow: 0 20px 40px rgba(0,0,0,0.2); overflow: hidden; }
                .confirm-icon { animation: scaleIn 0.5s ease-out; }
                @keyframes scaleIn { 0% { transform: scale(0); opacity: 0; } 50% { transform: scale(1.2); } 100% { transform: scale(1); opacity: 1; } }
                .table-warning { background-color: #fff3cd !important; }
            `}</style>
        </>
    );
}